import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

const TeacherAnalytics = () => {
  const routes = all_routes;
  const [selectedPeriod, setSelectedPeriod] = useState("current-month");
  const [selectedClass, setSelectedClass] = useState("all");

  const analyticsData = {
    overview: {
      totalStudents: 90,
      averageGrade: 85.6,
      attendanceRate: 94.2,
      homeworkSubmissionRate: 87.8,
      improvementTrend: "+2.3%",
    },
    gradeDistribution: [
      { grade: "A", count: 32, percentage: 35.6 },
      { grade: "B", count: 28, percentage: 31.1 },
      { grade: "C", count: 20, percentage: 22.2 },
      { grade: "D", count: 7, percentage: 7.8 },
      { grade: "F", count: 3, percentage: 3.3 },
    ],
    classPerformance: [
      {
        class: "Grade 10A",
        students: 30,
        avgGrade: 88.5,
        attendance: 96.2,
        submissions: 92.3,
      },
      {
        class: "Grade 11B",
        students: 28,
        avgGrade: 84.1,
        attendance: 93.5,
        submissions: 89.7,
      },
      {
        class: "Grade 9C",
        students: 32,
        avgGrade: 84.2,
        attendance: 93.0,
        submissions: 81.4,
      },
    ],
    topPerformers: [
      { name: "Emma Johnson", class: "10A", grade: 96.5, trend: "up" },
      { name: "Michael Chen", class: "11B", grade: 94.8, trend: "up" },
      { name: "Sarah Williams", class: "9C", grade: 93.2, trend: "stable" },
    ],
    strugglingStudents: [
      { name: "Alex Rodriguez", class: "10A", grade: 62.1, trend: "down" },
      { name: "Jessica Brown", class: "11B", grade: 65.8, trend: "down" },
      { name: "David Miller", class: "9C", grade: 68.3, trend: "stable" },
    ],
    monthlyTrends: [
      { month: "Jan", avgGrade: 82.1, attendance: 91.5, submissions: 85.2 },
      { month: "Feb", avgGrade: 83.4, attendance: 92.8, submissions: 86.7 },
      { month: "Mar", avgGrade: 84.2, attendance: 93.5, submissions: 87.1 },
      { month: "Apr", avgGrade: 85.6, attendance: 94.2, submissions: 87.8 },
    ],
  };

  const getGradeColor = (grade: number) => {
    if (grade >= 90) return "success";
    if (grade >= 80) return "info";
    if (grade >= 70) return "warning";
    return "danger";
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case "up":
        return { icon: "ti-trending-up", color: "success" };
      case "down":
        return { icon: "ti-trending-down", color: "danger" };
      default:
        return { icon: "ti-minus", color: "warning" };
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Teaching Analytics</h3>
              <p className="text-muted mb-0">
                Insights and performance metrics for your classes
              </p>
            </div>
            <div className="col-auto">
              <button className="btn btn-outline-primary">
                <i className="ti ti-download me-1"></i>
                Export Report
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Time Period</label>
                  <select
                    className="form-select"
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                  >
                    <option value="current-month">Current Month</option>
                    <option value="last-month">Last Month</option>
                    <option value="current-semester">Current Semester</option>
                    <option value="academic-year">Academic Year</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Class</label>
                  <select
                    className="form-select"
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                  >
                    <option value="all">All Classes</option>
                    <option value="10a">Grade 10A</option>
                    <option value="11b">Grade 11B</option>
                    <option value="9c">Grade 9C</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Overview Cards */}
          <div className="row mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card bg-primary-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-primary mb-2">
                    <i className="ti ti-users text-white fs-2"></i>
                  </div>
                  <h4 className="text-primary">
                    {analyticsData.overview.totalStudents}
                  </h4>
                  <p className="mb-0">Total Students</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-success-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-success mb-2">
                    <i className="ti ti-chart-line text-white fs-2"></i>
                  </div>
                  <h4 className="text-success">
                    {analyticsData.overview.averageGrade}%
                  </h4>
                  <p className="mb-0">Average Grade</p>
                  <small className="text-success">
                    {analyticsData.overview.improvementTrend}
                  </small>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-info-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-info mb-2">
                    <i className="ti ti-calendar-check text-white fs-2"></i>
                  </div>
                  <h4 className="text-info">
                    {analyticsData.overview.attendanceRate}%
                  </h4>
                  <p className="mb-0">Attendance Rate</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-warning-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-warning mb-2">
                    <i className="ti ti-file-upload text-white fs-2"></i>
                  </div>
                  <h4 className="text-warning">
                    {analyticsData.overview.homeworkSubmissionRate}%
                  </h4>
                  <p className="mb-0">Submission Rate</p>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Grade Distribution */}
            <div className="col-md-6 mb-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">Grade Distribution</h5>
                </div>
                <div className="card-body">
                  {analyticsData.gradeDistribution.map((item) => (
                    <div
                      key={item.grade}
                      className="d-flex align-items-center mb-3"
                    >
                      <div className="me-3">
                        <span className="fw-bold">Grade {item.grade}</span>
                      </div>
                      <div className="flex-fill">
                        <div className="progress" style={{ height: "8px" }}>
                          <div
                            className="progress-bar bg-primary"
                            style={{ width: `${item.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="ms-3 text-end">
                        <span className="small text-muted">
                          {item.count} students
                        </span>
                        <div className="fw-bold">{item.percentage}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Class Performance */}
            <div className="col-md-6 mb-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">Class Performance</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-borderless">
                      <thead>
                        <tr>
                          <th>Class</th>
                          <th>Avg Grade</th>
                          <th>Attendance</th>
                          <th>Submissions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsData.classPerformance.map((cls) => (
                          <tr key={cls.class}>
                            <td>
                              <div>
                                <h6 className="mb-0">{cls.class}</h6>
                                <small className="text-muted">
                                  {cls.students} students
                                </small>
                              </div>
                            </td>
                            <td>
                              <span
                                className={`badge bg-${getGradeColor(
                                  cls.avgGrade
                                )}`}
                              >
                                {cls.avgGrade}%
                              </span>
                            </td>
                            <td>{cls.attendance}%</td>
                            <td>{cls.submissions}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Top Performers */}
            <div className="col-md-6 mb-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Top Performers</h5>
                </div>
                <div className="card-body">
                  {analyticsData.topPerformers.map((student, index) => {
                    const trend = getTrendIcon(student.trend);
                    return (
                      <div
                        key={index}
                        className="d-flex align-items-center mb-3"
                      >
                        <div className="avatar avatar-sm bg-success-transparent me-3">
                          <span className="text-success fw-bold">
                            {index + 1}
                          </span>
                        </div>
                        <div className="flex-fill">
                          <h6 className="mb-0">{student.name}</h6>
                          <small className="text-muted">{student.class}</small>
                        </div>
                        <div className="text-end">
                          <div className="d-flex align-items-center">
                            <span className="fw-bold me-2">
                              {student.grade}%
                            </span>
                            <i
                              className={`${trend.icon} text-${trend.color}`}
                            ></i>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Students Needing Support */}
            <div className="col-md-6 mb-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Students Needing Support</h5>
                </div>
                <div className="card-body">
                  {analyticsData.strugglingStudents.map((student, index) => {
                    const trend = getTrendIcon(student.trend);
                    return (
                      <div
                        key={index}
                        className="d-flex align-items-center mb-3"
                      >
                        <div className="avatar avatar-sm bg-warning-transparent me-3">
                          <i className="ti ti-alert-triangle text-warning"></i>
                        </div>
                        <div className="flex-fill">
                          <h6 className="mb-0">{student.name}</h6>
                          <small className="text-muted">{student.class}</small>
                        </div>
                        <div className="text-end">
                          <div className="d-flex align-items-center">
                            <span className="fw-bold me-2">
                              {student.grade}%
                            </span>
                            <i
                              className={`${trend.icon} text-${trend.color}`}
                            ></i>
                          </div>
                          <button className="btn btn-sm btn-outline-primary mt-1">
                            Contact Parent
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Trends */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Monthly Trends</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Average Grade</th>
                      <th>Attendance Rate</th>
                      <th>Submission Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.monthlyTrends.map((month) => (
                      <tr key={month.month}>
                        <td className="fw-bold">{month.month}</td>
                        <td>
                          <span
                            className={`badge bg-${getGradeColor(
                              month.avgGrade
                            )}`}
                          >
                            {month.avgGrade}%
                          </span>
                        </td>
                        <td>{month.attendance}%</td>
                        <td>{month.submissions}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TeacherAnalytics;
